import { EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { useTranslations } from '@keymono/i18n';
import { useAuthStore } from '@keymono/services';
import { useUpdateUserDetailsMutation } from '@keymono/apis';
import {
  EmailAddressInputField,
  Button,
  FormFeedbackSection,
  useForm,
} from '@keymono/design-system';

import { CardWrapper } from '../styles';

interface IUpdateUserDetailsFormInputFields {
  email: string;
}

interface INamesCardEditProps {
  onExitEditMode: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * This card allows for the user to update their emails.
 * TODO: Refactor more to merge common items with NamesCard
 */
export function EmailsCardEdit({ onExitEditMode }: INamesCardEditProps) {
  const t = useTranslations('UserProfile.emailsSection');
  const user = useAuthStore().activeSession?.user;

  const handleCancelEdit = () => {
    onExitEditMode();
  };

  const handleOnUpdateNamesSuccess = () => {
    onExitEditMode();
  };

  const {
    error,
    isLoading: isFormSubmitting,
    updateUserDetails,
  } = useUpdateUserDetailsMutation({
    onSuccessCallback: handleOnUpdateNamesSuccess,
  });

  const errorFields = error?.errorFields;

  const handlePostDetails = (values: IUpdateUserDetailsFormInputFields) => {
    updateUserDetails({
      userId: user?.id || '',
      email: values.email || '',
    });
  };

  /**
   * The fields to be tracked within the form.
   */
  const emailsInputFieldsValues: IUpdateUserDetailsFormInputFields = {
    email: user?.email || '',
  };

  const {
    values,
    errors,
    handleOnInputChange,
    handleOnBlur,
    handleOnError,
    handleOnSubmitForm,
  } = useForm({
    initialValues: emailsInputFieldsValues,
    onSubmitCb: handlePostDetails,
  });

  return (
    <CardWrapper className="group">
      <div>
        <span className="inline-flex rounded-lg bg-teal-50 p-3 text-teal-700 ring-4 ring-white">
          <EnvelopeIcon className="h-6 w-6" aria-hidden="true" />
        </span>
      </div>
      <form className="mt-5 sm:mt-6" onSubmit={handleOnSubmitForm}>
        {error?.message ? (
          <FormFeedbackSection
            isFormSubmitting={isFormSubmitting}
            type="error"
            message={error.message}
            description={error.description}
          />
        ) : (
          <FormFeedbackSection
            isFormSubmitting={isFormSubmitting}
            type="info"
            message={t('editFormIntro.message')}
            description={t('editFormIntro.description')}
          />
        )}

        <EmailAddressInputField
          error={errorFields?.email || errors.email}
          label={t('formFields.email.label')}
          name="email"
          onBlurCallback={handleOnBlur}
          onChangeCallback={handleOnInputChange}
          onError={handleOnError}
          placeholder={t('formFields.email.placeholder')}
          value={values.email}
        />

        <div className="mt-2 flex w-full justify-end gap-2">
          <button
            type="button"
            onClick={handleCancelEdit}
            className="form-btn-secondary w-auto py-1.5 text-xs"
            disabled={isFormSubmitting}
          >
            {t('formFields.cancelCTA.label')}
          </button>
          <Button
            type="submit"
            className="form-btn w-auto px-8 py-1 text-xs"
            isEnabled={!isFormSubmitting}
            isLoading={isFormSubmitting}
            label={t('formFields.submitCTA.label')}
            loadingLabel={t('formFields.submitCTA.loadingLabel')}
          />
        </div>
      </form>
      <button
        type="button"
        title={t('closeCTATitle')}
        className="
          form-link-action
          absolute
          right-6 top-6 flex cursor-pointer rounded-lg
          p-2
          text-gray-300
          opacity-40
          hover:bg-red-100
          hover:text-red-700
          group-hover:text-red-700 group-hover:opacity-100
        "
        aria-hidden="true"
        onClick={handleCancelEdit}
      >
        <XMarkIcon className="h-6 w-6" />
      </button>
    </CardWrapper>
  );
}
